import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Pencil, Trash2, X, Save, ListChecks, Loader2, Paperclip, CheckSquare, Upload } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface OnboardingStep { id: string; label: string; type: 'checkbox' | 'upload'; description: string; required: boolean; }

interface OnboardingTemplate {
    id: string;
    name: string;
    attachment_name: string;
    attachment_data: string | null;
    steps: OnboardingStep[];
    is_active: boolean;
}

const inputCls = "w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

const emptyStep = (): OnboardingStep => ({ id: `s${Date.now()}${Math.random().toString(36).slice(2, 6)}`, label: '', type: 'checkbox', description: '', required: true });

function TemplateFormModal({ initial, onClose, onSaved }: { initial: OnboardingTemplate | null; onClose: () => void; onSaved: () => void }) {
    const [name, setName] = React.useState(initial?.name ?? '');
    const [attachmentName, setAttachmentName] = React.useState(initial?.attachment_name ?? '');
    const [attachmentData, setAttachmentData] = React.useState<string | null>(initial?.attachment_data ?? null);
    const [steps, setSteps] = React.useState<OnboardingStep[]>(initial?.steps?.length ? initial.steps : [emptyStep()]);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
    const updateStep = (id: string, patch: Partial<OnboardingStep>) => setSteps((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
    const removeStep = (id: string) => setSteps((prev) => prev.filter((s) => s.id !== id));

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setAttachmentData(reader.result as string);
            setAttachmentName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!name.trim()) { setError('Le nom est requis.'); return; }
        const cleanSteps = steps.filter((s) => s.label.trim());
        if (cleanSteps.length === 0) { setError('Ajoutez au moins une étape.'); return; }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: name.trim(), attachment_name: attachmentName, attachment_data: attachmentData,
                steps: cleanSteps, is_active: initial?.is_active ?? true,
            };
            if (initial) await moduleApi.updateOnboardingTemplate(initial.id, payload);
            else await moduleApi.createOnboardingTemplate(payload);
            onSaved();
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                        <h2 className="text-base font-bold text-content">{initial ? 'Modifier le parcours' : 'Nouveau parcours'}</h2>
                        <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4 overflow-y-auto">
                        <div>
                            <label className={labelCls}>Nom du parcours</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Ex: Intégration Restauration" />
                        </div>

                        <div>
                            <label className={labelCls}>Document à consulter (optionnel)</label>
                            <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface-soft border border-border text-sm text-content-muted hover:text-content cursor-pointer transition-colors">
                                <Paperclip size={14} />
                                {attachmentName || 'Choisir un fichier (ex: règlement intérieur)'}
                                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileChange} />
                            </label>
                            <p className="text-[11px] text-content-muted mt-1">Le salarié pourra le télécharger sur l'étape qui y fait référence.</p>
                        </div>

                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-content flex items-center gap-1.5"><ListChecks size={14} /> Étapes</span>
                                <button onClick={addStep} type="button" className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-[11px]">
                                    <Plus size={12} /> Ajouter
                                </button>
                            </div>
                            <p className="text-[11px] text-content-muted">"Case à cocher" pour une étape simple (ex: signer le contrat, lire un document) ou "Document à fournir" pour demander un fichier au salarié (ex: pièce d'identité).</p>
                            <div className="space-y-2.5">
                                {steps.map((s) => (
                                    <div key={s.id} className="p-2.5 rounded-lg bg-surface border border-border space-y-2">
                                        <div className="flex items-center gap-1.5">
                                            <input type="text" value={s.label} onChange={(e) => updateStep(s.id, { label: e.target.value })}
                                                placeholder="Ex: Fournir sa pièce d'identité" className="flex-1 min-w-0 bg-transparent text-sm text-content outline-none" />
                                            <button onClick={() => removeStep(s.id)} className="text-red-400 shrink-0"><Trash2 size={13} /></button>
                                        </div>
                                        <input type="text" value={s.description} onChange={(e) => updateStep(s.id, { description: e.target.value })}
                                            placeholder="Précision affichée au salarié (optionnel)" className="w-full bg-surface-soft border border-border rounded-lg px-2 py-1 text-xs text-content outline-none" />
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex gap-2">
                                                {(['checkbox', 'upload'] as const).map((t) => (
                                                    <button key={t} type="button" onClick={() => updateStep(s.id, { type: t })}
                                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all ${s.type === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {t === 'checkbox' ? <CheckSquare size={12} /> : <Upload size={12} />} {t === 'checkbox' ? 'Case à cocher' : 'Document à fournir'}
                                                    </button>
                                                ))}
                                            </div>
                                            <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                                                <input type="checkbox" checked={s.required} onChange={(e) => updateStep(s.id, { required: e.target.checked })} className="accent-brand-600 w-3.5 h-3.5" />
                                                <span className="text-[10px] font-bold text-content-muted uppercase tracking-wide">Obligatoire</span>
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    </div>
                    <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                        <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50">
                            <Save size={15} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

export const OnboardingTemplatesSettings: React.FC = () => {
    const [templates, setTemplates] = React.useState<OnboardingTemplate[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showForm, setShowForm] = React.useState(false);
    const [editing, setEditing] = React.useState<OnboardingTemplate | null>(null);

    const load = () => {
        moduleApi.getOnboardingTemplates().then((d: any) => setTemplates(Array.isArray(d) ? d : [])).catch(() => setTemplates([])).finally(() => setLoading(false));
    };
    React.useEffect(() => { load(); }, []);

    const toggleActive = async (t: OnboardingTemplate) => {
        await moduleApi.updateOnboardingTemplate(t.id, { ...t, is_active: !t.is_active });
        load();
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Supprimer ce parcours ? Les parcours déjà assignés à des salariés restent inchangés.')) return;
        await moduleApi.deleteOnboardingTemplate(id);
        load();
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-3xl space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-content-muted italic max-w-md">Créez des parcours d'intégration réutilisables (étapes à cocher ou documents à fournir) à assigner à un nouveau salarié depuis sa fiche.</p>
                <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shrink-0 transition-all">
                    <Plus size={16} /> Nouveau parcours
                </button>
            </div>

            {templates.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <ListChecks size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun parcours pour l'instant.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {templates.map((t) => (
                        <div key={t.id} className="flex items-center gap-4 p-4 rounded-2xl bg-surface-soft border border-border">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-bold text-content text-sm">{t.name}</p>
                                    {!t.is_active && <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-surface border border-border text-content-muted">Désactivé</span>}
                                </div>
                                <p className="text-xs text-content-muted mt-0.5">{t.steps.length} étape{t.steps.length > 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button onClick={() => toggleActive(t)} className="px-3 py-2 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold transition-all">
                                    {t.is_active ? 'Désactiver' : 'Activer'}
                                </button>
                                <button onClick={() => { setEditing(t); setShowForm(true); }} className="p-2 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 transition-all"><Pencil size={15} /></button>
                                <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showForm && <TemplateFormModal initial={editing} onClose={() => setShowForm(false)} onSaved={load} />}
        </div>
    );
};
